import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputBase,
  Button,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { get } from "../../helpers/aws_api_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import * as url from "../../helpers/url_helper";
import { summaryTheme as t } from "./styles";
import { getComplianceIcon } from "../../assets/images/compliance/IconCompliance.jsx";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Stable default so hooks do not see a new `[]` every render when prop is omitted. */
const DEFAULT_MODULE_FILTERS = [];

/** Widths sum to 100%; Security and Compliance share equal share to avoid a wide empty gap. */
const DETAIL_TABLE_COLUMNS = [
  { id: "arn", label: "ARN(s)", desc: "Amazon Resource Name — unique identifier for the resource", width: "26%", minWidth: 200 },
  { id: "service", label: "Service", desc: "AWS service or product (e.g. S3, IAM, EC2)", width: "10%" },
  { id: "region", label: "Region", desc: "AWS region where the resource is located", width: "9%" },
  { id: "security_failed_checks", label: "Security", desc: "Security Findings, hover for remediations", width: "11%" },
  {
    id: "compliance_frameworks",
    label: "Compliance",
    desc: "Compliance Findings, click for remediations",
    width: "11%",
    align: "center",
  },
  {
    id: "compliance_control_id_count",
    label: "Control ID",
    desc: "Control ID Findings, click for remediations",
    width: "10%",
    minWidth: 88,
    align: "center",
  },
  {
    id: "cloud_waste",
    label: "Cloud Waste",
    desc: "Estimated Monthly Savings, hover for recommended actions",
    width: "10%",
    align: "center",
  },
  { id: "cost_unblended_mtd", label: "Cost (Last 30 Days)", desc: "Unblended cost for this resource in the last 30 days", width: "13%", align: "right" },
];

function formatCost(value) {
  if (value == null || (typeof value !== "number" && typeof value !== "string")) return "—";
  const num = typeof value === "string" ? parseFloat(value, 10) : value;
  if (Number.isNaN(num)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/** Frameworks with findings — matches the number shown on the Compliance chip. */
function complianceFrameworksCount(row) {
  const failedByFramework = row?.compliance_failed_controls;
  if (failedByFramework && typeof failedByFramework === "object" && !Array.isArray(failedByFramework)) {
    return Object.keys(failedByFramework).length;
  }
  return 0;
}

/** Count control IDs from `compliance_failed_controls` values (arrays, numbers, or nested control_ids). */
function countControlsFromFailedMap(map) {
  if (!map || typeof map !== "object" || Array.isArray(map)) return 0;
  return Object.values(map).reduce((sum, val) => {
    if (Array.isArray(val)) return sum + val.length;
    if (typeof val === "number" && Number.isFinite(val)) return sum + val;
    if (val && typeof val === "object" && Array.isArray(val.control_ids)) return sum + val.control_ids.length;
    if (typeof val === "string" && val.trim()) return sum + 1;
    return sum;
  }, 0);
}

/**
 * Total control IDs: prefer sum of control_ids on compliance_remediations; if none, use failed-controls map;
 * last resort compliance_failed_total (API aggregate when detail arrays are omitted).
 */
function countRemediationControlIds(row) {
  const list = Array.isArray(row?.compliance_remediations) ? row.compliance_remediations : [];
  const fromRem = list.reduce((sum, item) => {
    const ids = Array.isArray(item?.control_ids) ? item.control_ids : [];
    return sum + ids.length;
  }, 0);
  if (fromRem > 0) return fromRem;
  const fromMap = countControlsFromFailedMap(row?.compliance_failed_controls);
  if (fromMap > 0) return fromMap;
  return Number(row?.compliance_failed_total) || 0;
}

/** Unique framework keys for the compliance dialog (remediations + failed-control maps). */
function dialogFrameworkList(row) {
  if (!row) return [];
  const s = new Set();
  if (Array.isArray(row.compliance_remediations)) {
    row.compliance_remediations.forEach((r) => {
      if (r?.framework) s.add(String(r.framework));
    });
  }
  const cidMap = row.compliance_failed_control_ids;
  if (cidMap && typeof cidMap === "object" && !Array.isArray(cidMap)) {
    Object.keys(cidMap).forEach((k) => s.add(String(k)));
  }
  const cf = row.compliance_failed_controls;
  if (cf && typeof cf === "object" && !Array.isArray(cf)) {
    Object.keys(cf).forEach((k) => s.add(String(k)));
  }
  return Array.from(s).sort((a, b) => a.localeCompare(b));
}

/** Control IDs for one framework (remediations + `compliance_failed_control_ids`); empty framework = union across all. */
function dialogControlIdsForFramework(row, framework) {
  const ids = new Set();
  if (!row) return [];
  const fw = (framework || "").trim();
  if (fw) {
    if (Array.isArray(row.compliance_remediations)) {
      row.compliance_remediations.forEach((r) => {
        if (r.framework === fw) {
          (Array.isArray(r.control_ids) ? r.control_ids : []).forEach((c) => ids.add(String(c)));
        }
      });
    }
    const m = row.compliance_failed_control_ids?.[fw];
    if (Array.isArray(m)) m.forEach((c) => ids.add(String(c)));
    return Array.from(ids).sort((a, b) => a.localeCompare(b));
  }
  dialogFrameworkList(row).forEach((f) => {
    dialogControlIdsForFramework(row, f).forEach((c) => ids.add(c));
  });
  return Array.from(ids).sort((a, b) => a.localeCompare(b));
}

/** Frameworks that include this control ID for the given row. */
function dialogFrameworksForControlId(row, controlId) {
  if (!row) return [];
  const cid = (controlId || "").trim();
  if (!cid) return dialogFrameworkList(row);
  return dialogFrameworkList(row).filter((fw) => dialogControlIdsForFramework(row, fw).includes(cid));
}

/** Client refinement when API sends repeated `module` filters (AND semantics). */
function rowMatchesModuleClientFilter(row, moduleFilters) {
  if (!Array.isArray(moduleFilters) || moduleFilters.length === 0) return true;
  return moduleFilters.every((part) => {
    if (part === "security") return Number(row.security_failed_checks) > 0;
    if (part === "compliance") return Number(row.compliance_failed_total) > 0;
    if (part === "cost") return Number(row.cost_unblended_mtd) > 0;
    if (part === "cloud_waste") return row.cloud_waste === true;
    return true;
  });
}

function formatDate(dateString) {
  if (!dateString) return "—";
  try {
    const parts = String(dateString).split("-");
    if (parts.length >= 3) {
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      const d = parseInt(parts[2], 10);
      if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
        return new Date(y, m, d).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
    }
    return String(dateString);
  } catch {
    return String(dateString);
  }
}

/** Shadcn-style tooltip: header + list (shown on hover) */
const complianceTooltipSx = {
  fontFamily: t.fontFamily,
  maxWidth: 320,
  padding: 0,
  borderRadius: t.radius.lg,
  border: `1px solid ${t.colors.border}`,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.02)",
  backgroundColor: t.colors.card,
  // Allow inner content (like scrollable lists) to render fully
  overflow: "visible",
};

/** Compliance cell: status chip or count; click opens modal in parent via onOpenDetails */
function ComplianceCell({ row, onOpenDetails }) {
  const remediations = Array.isArray(row.compliance_remediations)
    ? row.compliance_remediations
    : [];
  const hasRemediations = remediations.length > 0;
  const totalControls = Number(row.compliance_failed_total) || 0;
  const frameworksCount = complianceFrameworksCount(row);

  const statusLower = (row.compliance_status || "").toLowerCase();
  if (statusLower === "unknown" || statusLower === "unknow") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontFamily: t.fontFamily,
            color: t.colors.textMuted,
          }}
        >
          —
        </Typography>
      </Box>
    );
  }

  const tooltipContent = hasRemediations ? (
    <Box sx={{ fontFamily: t.fontFamily, maxWidth: 360 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.borderLight,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: t.colors.text,
          }}
        >
          Compliance remediations
        </Typography>
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: t.colors.textMuted,
            mt: 0.25,
          }}
        >
          {frameworksCount} framework{frameworksCount === 1 ? "" : "s"} ·{" "}
          {totalControls} failed control{totalControls === 1 ? "" : "s"} ·{" "}
          {remediations.length} remediation item
          {remediations.length === 1 ? "" : "s"}
        </Typography>
      </Box>
      <Box sx={{ maxHeight: 260, overflowY: "auto", py: 1 }}>
        {remediations.map((item, idx) => {
          const iconSrc = getComplianceIcon(item.framework);
          return (
            <Box
              key={`${item.framework || "fw"}-${idx}`}
              sx={{
                px: 2,
                py: 0.75,
                borderBottom: `1px solid ${t.colors.borderLight}`,
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              {iconSrc && (
                <Box
                  component="img"
                  src={iconSrc}
                  alt={item.framework}
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 1,
                    objectFit: "contain",
                    mt: 0.25,
                    flexShrink: 0,
                  }}
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: t.colors.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mb: 0.25,
                  }}
                >
                  {item.framework || "Framework"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: t.colors.text,
                    mb: 0.25,
                  }}
                >
                  {item.title}
                </Typography>
                {item.url && (
                  <Typography
                    component="a"
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "inline-block",
                      fontSize: "0.72rem",
                      color: t.colors.accent,
                      textDecoration: "none",
                      mb: 0.25,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    View remediation
                  </Typography>
                )}
                {Array.isArray(item.control_ids) && item.control_ids.length > 0 && (
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color: t.colors.textMuted,
                    }}
                  >
                    Controls: {item.control_ids.join(", ")}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  ) : null;

  const isUnknown =
    (row.compliance_status || "").toLowerCase() === "unknown" ||
    (row.compliance_status || "").toLowerCase() === "unknow";
  const chip = isUnknown ? (
    <Typography
      sx={{
        fontSize: "0.75rem",
        fontFamily: t.fontFamily,
        color: t.colors.textMuted,
      }}
    >
      —
    </Typography>
  ) : (
    <StatusChip
      status={row.compliance_status}
      label={String(frameworksCount)}
    />
  );

  if (typeof onOpenDetails === "function") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
        <Box
          component="button"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetails(row, { openedFrom: "compliance" });
          }}
          sx={{
            all: "unset",
            cursor: hasRemediations ? "pointer" : "default",
            display: "inline-flex",
            justifyContent: "center",
          }}
        >
          {chip}
        </Box>
      </Box>
    );
  }

  if (tooltipContent) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Tooltip
          title={tooltipContent}
          placement="top-start"
          enterDelay={200}
          leaveDelay={0}
          componentsProps={{ tooltip: { sx: complianceTooltipSx } }}
        >
          <Box component="span" sx={{ display: "inline-block", cursor: "default" }}>
            {chip}
          </Box>
        </Tooltip>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>{chip}</Box>
  );
}

/** Control ID total in the same StatusChip style as Compliance; click opens the compliance details dialog. */
function ControlIdCell({ row, onOpenDetails }) {
  const count = countRemediationControlIds(row);
  const remediations = Array.isArray(row.compliance_remediations) ? row.compliance_remediations : [];
  const hasRemediations = remediations.length > 0;
  const canOpen = typeof onOpenDetails === "function";

  const statusLower = (row.compliance_status || "").toLowerCase();
  if (statusLower === "unknown" || statusLower === "unknow") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Typography sx={{ fontSize: "0.75rem", fontFamily: t.fontFamily, color: t.colors.textMuted }}>—</Typography>
      </Box>
    );
  }

  const chip = <StatusChip status={row.compliance_status} label={String(count)} />;

  if (!canOpen) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>{chip}</Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <Box
        component="button"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenDetails(row, { openedFrom: "control_id" });
        }}
        sx={{
          all: "unset",
          cursor: hasRemediations ? "pointer" : "default",
          display: "inline-flex",
        }}
      >
        {chip}
      </Box>
    </Box>
  );
}

function StatusChip({ status, label }) {
  const isFailed = (status || "").toLowerCase() === "failed";
  return (
    <Chip
      size="small"
      label={label ?? (isFailed ? "Failed" : "Passed")}
      sx={{
        fontWeight: 600,
        fontSize: "0.6875rem",
        fontFamily: t.fontFamily,
        borderRadius: t.radius.sm,
        height: 20,
        ...(isFailed
          ? { bgcolor: t.colors.destructiveBg, color: t.colors.destructive, border: `1px solid ${t.colors.destructiveBorder}` }
          : { bgcolor: t.colors.successBg, color: t.colors.success }),
      }}
    />
  );
}

const SECURITY_SEVERITY_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];
const SECURITY_SEVERITY_RANK = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};
const SECURITY_SEVERITY_COLORS = {
  CRITICAL: t.colors.severity.critical,
  HIGH: t.colors.severity.high,
  MEDIUM: t.colors.severity.medium,
  LOW: t.colors.severity.low,
};

function securitySeverityDisplay(severity) {
  if (severity == null || severity === "") return null;
  const upper = String(severity).trim().toUpperCase();
  const label = upper.charAt(0) + upper.slice(1).toLowerCase();
  return { label, color: SECURITY_SEVERITY_COLORS[upper] || t.colors.textMuted };
}

/** "MigrateToGraviton" -> "Migrate To Graviton" */
function formatCloudWasteActionLabel(key) {
  if (!key) return "—";
  return String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** Cloud waste: formatted total; hover shows est. monthly savings split by action */
function CloudWasteCell({ row }) {
  if (row.cloud_waste !== true) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Typography sx={{ fontSize: "0.75rem", fontFamily: t.fontFamily, color: t.colors.textMuted, fontVariantNumeric: "tabular-nums" }}>
          —
        </Typography>
      </Box>
    );
  }

  const total = row.cloud_waste_savings_total;
  const rawMap = row.cloud_waste_savings_by_action;
  const entries =
    rawMap && typeof rawMap === "object"
      ? Object.entries(rawMap)
          .map(([k, v]) => [k, Number(v)])
          .filter(([, n]) => Number.isFinite(n) && n > 0)
          .sort((a, b) => b[1] - a[1])
      : [];

  const display = formatCost(total);

  const tooltipContent = (
    <Box sx={{ fontFamily: t.fontFamily }}>
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.borderLight,
        }}
      >
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: t.colors.text }}>Est. Monthly Savings</Typography>
        <Typography sx={{ fontSize: "0.72rem", color: t.colors.textMuted, mt: 0.25 }}>
          Cloud waste by recommended action
        </Typography>
      </Box>
      <Box sx={{ px: 2, py: 1.25, maxWidth: 280 }}>
        {entries.length > 0 ? (
          <>
            {entries.map(([key, amt]) => (
              <Typography key={key} sx={{ fontSize: "0.75rem", color: t.colors.textMuted, mb: 0.35 }}>
                {formatCloudWasteActionLabel(key)}: {formatCost(amt)}
              </Typography>
            ))}
            {entries.length > 1 && (
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: t.colors.text,
                  mt: 0.75,
                  pt: 0.75,
                  borderTop: `1px solid ${t.colors.borderLight}`,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                Monthly total: {formatCost(total)}
              </Typography>
            )}
          </>
        ) : (
          <Typography sx={{ fontSize: "0.75rem", color: t.colors.textMuted, fontVariantNumeric: "tabular-nums" }}>{display}</Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <Tooltip
        title={tooltipContent}
        placement="top-start"
        enterDelay={200}
        leaveDelay={0}
        componentsProps={{ tooltip: { sx: complianceTooltipSx } }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: "0.75rem",
            fontFamily: t.fontFamily,
            fontVariantNumeric: "tabular-nums",
            color: t.colors.text,
            cursor: "default",
            display: "inline-block",
          }}
        >
          {display}
        </Typography>
      </Tooltip>
    </Box>
  );
}

/** Security column: show per-severity counts with remediation tooltip (using security_* fields) */
function SecurityCell({ row }) {
  const remediations = Array.isArray(row.security_remediations)
    ? row.security_remediations
    : [];
  const sortedRemediations = [...remediations].sort((a, b) => {
    const aKey = String(a?.severity || "").trim().toUpperCase();
    const bKey = String(b?.severity || "").trim().toUpperCase();
    const aRank = SECURITY_SEVERITY_RANK[aKey] ?? Number.MAX_SAFE_INTEGER;
    const bRank = SECURITY_SEVERITY_RANK[bKey] ?? Number.MAX_SAFE_INTEGER;
    if (aRank !== bRank) return aRank - bRank;
    const aId = String(a?.check_id || a?.checkId || a?.title || "");
    const bId = String(b?.check_id || b?.checkId || b?.title || "");
    return aId.localeCompare(bId);
  });
  const hasRemediations = remediations.length > 0;
  const totalChecks = Number(row.security_failed_checks) || 0;
  const bySeverity = row.security_failed_checks_by_severity || {};

  const tooltipContent = (
    <Box sx={{ fontFamily: t.fontFamily, maxWidth: 360 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.borderLight,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: t.colors.text,
            fontWeight: 600,
          }}
        >
          {totalChecks} Failed Checks
        </Typography>
      </Box>
      <Box sx={{ maxHeight: 260, overflowY: "auto", py: 1 }}>
        {sortedRemediations.map((item, idx) => {
          const sev = securitySeverityDisplay(item.severity);
          const checkId = item?.check_id || item?.checkId || "";
          return (
          <Box
            key={idx}
            sx={{
              px: 2,
              py: 0.75,
              borderBottom: `1px solid ${t.colors.borderLight}`,
            }}
          >
            <Box
              sx={{
                border: `1px solid ${t.colors.border}`,
                borderRadius: t.radius.sm,
                backgroundColor: t.colors.card,
                px: 1,
                py: 0.75,
              }}
            >
              {checkId && (
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: t.colors.textMuted,
                    mb: 0.35,
                  }}
                >
                  {checkId}
                </Typography>
              )}
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  color: t.colors.text,
                  lineHeight: 1.35,
                  mb: 0.5,
                }}
              >
                {item.title}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                {item.url ? (
                  <Typography
                    component="a"
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "inline-block",
                      fontSize: "0.72rem",
                      color: t.colors.accent,
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    View remediation
                  </Typography>
                ) : (
                  <Box />
                )}
                {sev && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      fontFamily: t.fontFamily,
                      color: sev.color,
                      flexShrink: 0,
                      lineHeight: 1.3,
                    }}
                  >
                    {sev.label}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          );
        })}
      </Box>
    </Box>
  );

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, width: "100%", minWidth: 0 }}>
      {SECURITY_SEVERITY_ORDER.map((sev) => {
        const count =
          Number(
            bySeverity && typeof bySeverity === "object" ? bySeverity[sev] : 0
          ) || 0;
        if (count <= 0) return null;
        const label = sev.charAt(0) + sev.slice(1).toLowerCase(); // CRITICAL -> Critical
        return (
          <Typography
            key={sev}
            sx={{
              fontSize: "0.75rem",
              fontFamily: t.fontFamily,
              color: SECURITY_SEVERITY_COLORS[sev] || t.colors.text,
            }}
          >
            {label} ({count})
          </Typography>
        );
      })}
      {/* If no severities have counts, show a simple dash */}
      {(!bySeverity ||
        SECURITY_SEVERITY_ORDER.every((sev) => {
          const count =
            Number(
              bySeverity && typeof bySeverity === "object" ? bySeverity[sev] : 0
            ) || 0;
          return count <= 0;
        })) && (
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontFamily: t.fontFamily,
            color: t.colors.textMuted,
          }}
        >
          —
        </Typography>
      )}
    </Box>
  );

  if (!hasRemediations) {
    return content;
  }

  return (
    <Tooltip
      title={tooltipContent}
      placement="top-start"
      enterDelay={200}
      leaveDelay={0}
      componentsProps={{ tooltip: { sx: complianceTooltipSx } }}
    >
      <Box component="span" sx={{ display: "inline-block", cursor: "default" }}>
        {content}
      </Box>
    </Tooltip>
  );
}

/**
 * Account detail content: KPIs, service filter, resources table.
 * Used by Summary page (with accountNumber from dropdown) and by /summary/account/:id route.
 * When accounts/onAccountChange/getAccountLabel are provided, header shows only account dropdown (top right).
 */
export default function SummaryAccountDetailContent({
  accountNumber,
  showBackLink = false,
  accounts = [],
  accountsLoading = false,
  accountsError = null,
  onAccountChange,
  getAccountLabel,
  headerOnly = false,
  severity = "",
  onSeverityChange,
  framework = "",
  onFrameworkChange,
  frameworks = [],
  moduleFilters = DEFAULT_MODULE_FILTERS,
  kpiTableFilterLabel = "",
  onClearKpiTableFilter,
  /** When `onSelectedServiceChange` is set, service filter is controlled by the parent (e.g. Summary chart bar clicks). */
  selectedService: selectedServiceProp,
  onSelectedServiceChange,
}) {
  const fallbackComplianceIcon = getComplianceIcon("aws");
  const history = useHistory();
  const [details, setDetails] = useState([]);
  const [detailsTotal, setDetailsTotal] = useState(0);
  const [detailsPage, setDetailsPage] = useState(1);
  const [detailsPageSize, setDetailsPageSize] = useState(25);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [servicesList, setServicesList] = useState([]);
  const [internalSelectedService, setInternalSelectedService] = useState("");
  const isServiceControlled = typeof onSelectedServiceChange === "function";
  const selectedService = isServiceControlled ? (selectedServiceProp ?? "") : internalSelectedService;
  const setSelectedService = useCallback(
    (value) => {
      if (isServiceControlled) onSelectedServiceChange(value);
      else setInternalSelectedService(value);
    },
    [isServiceControlled, onSelectedServiceChange]
  );
  const [tableSearch, setTableSearch] = useState("");
  const [sortBy, setSortBy] = useState("arn");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedRow, setSelectedRow] = useState(null);
  const [complianceDialogRow, setComplianceDialogRow] = useState(null);
  /** "compliance" = opened from Compliance column; "control_id" = Control ID column first (filters + table order). */
  const [complianceDialogLayout, setComplianceDialogLayout] = useState("compliance");
  const [complianceFrameworkFilter, setComplianceFrameworkFilter] = useState("");
  const [complianceControlFilter, setComplianceControlFilter] = useState("");

  /** Stable across array reference churn — avoids fetchDetails/useEffect re-running every render. */
  const moduleFiltersKey = useMemo(() => {
    if (!Array.isArray(moduleFilters) || moduleFilters.length === 0) return "";
    return [...moduleFilters].filter(Boolean).sort().join("\0");
  }, [moduleFilters]);

  useLayoutEffect(() => {
    setDetailsPage((p) => (p === 1 ? p : 1));
  }, [moduleFiltersKey, severity, framework, accountNumber, selectedService]);

  useEffect(() => {
    if (!accountNumber) return;

    let cancelled = false;

    (async () => {
      setDetailsLoading(true);
      setDetailsError(null);
      try {
        const detailsPath = `${url.ARN_VIEWS_BASE}/accounts/${encodeURIComponent(accountNumber)}/details`;
        const sp = new URLSearchParams();
        sp.set("page", String(detailsPage));
        sp.set("page_size", String(detailsPageSize));
        if (selectedService) sp.set("service", selectedService);
        if (severity) sp.set("severity", severity);
        if (framework) sp.set("framework", framework);
        if (Array.isArray(moduleFilters)) {
          moduleFilters.forEach((m) => {
            if (m) sp.append("module", m);
          });
        }
        const response = await get(`${detailsPath}?${sp.toString()}`);
        if (cancelled) return;
        const status = response?.status;
        if (status >= 200 && status < 300) {
          const items = response?.items ?? [];
          setDetails(Array.isArray(items) ? items : []);
          setDetailsTotal(response?.total ?? items.length);
        } else {
          setDetailsError(getApiErrorMessage(response) || "Failed to load details");
        }
      } catch (err) {
        if (cancelled) return;
        const errData = err?.data ?? err?.response?.data;
        setDetailsError(getApiErrorMessage(errData) || err?.message || "Failed to load details");
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `moduleFilters` from closure; `moduleFiltersKey` avoids unstable `[]` reference retriggering fetch
  }, [
    accountNumber,
    detailsPage,
    detailsPageSize,
    selectedService,
    severity,
    framework,
    moduleFiltersKey,
  ]);

  const fetchServices = useCallback(async () => {
    if (!accountNumber) return;
    try {
      const response = await get(
        `${url.ARN_VIEWS_BASE}/accounts/${encodeURIComponent(accountNumber)}/summary`
      );
      const status = response?.status;
      if (status >= 200 && status < 300) {
        const list = response?.services ?? response?.items ?? (Array.isArray(response) ? response : []);
        setServicesList(Array.isArray(list) ? list : []);
      } else {
        setServicesList([]);
      }
    } catch {
      setServicesList([]);
    }
  }, [accountNumber]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleChangePage = (_, newPage) => setDetailsPage(newPage + 1);
  const handleChangeRowsPerPage = (e) => {
    setDetailsPageSize(parseInt(e.target.value, 10));
    setDetailsPage(1);
  };

  const filteredAndSortedDetails = useMemo(() => {
    let list = [...details];
    const q = (tableSearch || "").trim().toLowerCase();
    if (q) {
      list = list.filter((row) => {
        const arn = (row.arn || "").toLowerCase();
        const service = (row.service || "").toLowerCase();
        const region = (row.region || "").toLowerCase();
        const modules = Array.isArray(row.modules) ? row.modules.join(" ").toLowerCase() : "";
        return arn.includes(q) || service.includes(q) || region.includes(q) || modules.includes(q);
      });
    }
    if (Array.isArray(moduleFilters) && moduleFilters.length > 0) {
      list = list.filter((row) => rowMatchesModuleClientFilter(row, moduleFilters));
    }
    list.sort((a, b) => {
      if (sortBy === "compliance_frameworks") {
        const af = complianceFrameworksCount(a);
        const bf = complianceFrameworksCount(b);
        let cmp = af - bf;
        if (cmp === 0) cmp = (Number(a.compliance_failed_total) || 0) - (Number(b.compliance_failed_total) || 0);
        if (cmp === 0) cmp = String(a.arn || "").localeCompare(String(b.arn || ""));
        return sortOrder === "asc" ? cmp : -cmp;
      }

      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (sortBy === "arn") {
        aVal = (a.arn || "").toLowerCase();
        bVal = (b.arn || "").toLowerCase();
      } else if (sortBy === "cloud_waste") {
        aVal = a.cloud_waste === true ? Number(a.cloud_waste_savings_total) || 0 : 0;
        bVal = b.cloud_waste === true ? Number(b.cloud_waste_savings_total) || 0 : 0;
      } else if (sortBy === "compliance_control_id_count") {
        aVal = countRemediationControlIds(a);
        bVal = countRemediationControlIds(b);
      } else if (sortBy === "cost_unblended_mtd") {
        aVal = Number.parseFloat(a.cost_unblended_mtd);
        bVal = Number.parseFloat(b.cost_unblended_mtd);
        if (Number.isNaN(aVal)) aVal = 0;
        if (Number.isNaN(bVal)) bVal = 0;
      } else if (sortBy === "security_failed_checks") {
        aVal = Number(a.security_failed_checks) || 0;
        bVal = Number(b.security_failed_checks) || 0;
      } else if (sortBy === "snapshot_date") {
        aVal = a.snapshot_date || "";
        bVal = b.snapshot_date || "";
      } else {
        aVal = (aVal ?? "").toString().toLowerCase();
        bVal = (bVal ?? "").toString().toLowerCase();
      }
      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [details, tableSearch, sortBy, sortOrder, moduleFiltersKey, moduleFilters]);

  const handleSort = (columnId) => {
    if (sortBy === columnId) setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    else {
      setSortBy(columnId);
      setSortOrder("asc");
    }
  };

  const pageKpis = useMemo(() => {
    let securityFailed = 0;
    let complianceFailed = 0;
    let costMtd = 0;
    filteredAndSortedDetails.forEach((row) => {
      securityFailed += Number(row.security_failed_checks) || 0;
      complianceFailed += Number(row.compliance_failed_total) || 0;
      const c = row.cost_unblended_mtd;
      if (c != null && c !== "") costMtd += Number(c) || 0;
    });
    return { securityFailed, complianceFailed, costMtd };
  }, [filteredAndSortedDetails]);

  const complianceDialogRemediations = useMemo(() => {
    if (!complianceDialogRow || !Array.isArray(complianceDialogRow.compliance_remediations)) {
      return [];
    }
    const list = complianceDialogRow.compliance_remediations;
    const fw = (complianceFrameworkFilter || "").trim();
    const selectedControl = (complianceControlFilter || "").trim();

    return list.filter((item) => {
      const matchesFramework = fw ? item.framework === fw : true;
      if (!matchesFramework) return false;
      if (!selectedControl) return true;
      const controls = Array.isArray(item.control_ids) ? item.control_ids : [];
      return controls.includes(selectedControl);
    });
  }, [complianceDialogRow, complianceFrameworkFilter, complianceControlFilter]);

  const complianceDialogFrameworkOptions = useMemo(() => {
    if (!complianceDialogRow) return [];
    const cid = (complianceControlFilter || "").trim();
    if (!cid) return dialogFrameworkList(complianceDialogRow);
    return dialogFrameworksForControlId(complianceDialogRow, cid);
  }, [complianceDialogRow, complianceControlFilter]);

  const complianceDialogControlOptions = useMemo(() => {
    if (!complianceDialogRow) return [];
    return dialogControlIdsForFramework(
      complianceDialogRow,
      (complianceFrameworkFilter || "").trim()
    );
  }, [complianceDialogRow, complianceFrameworkFilter]);

  const handleComplianceDialogFrameworkChange = (e) => {
    const fw = e.target.value;
    setComplianceFrameworkFilter(fw);
    if (!complianceDialogRow) return;
    const allowed = new Set(dialogControlIdsForFramework(complianceDialogRow, fw));
    const cur = (complianceControlFilter || "").trim();
    if (cur && !allowed.has(cur)) setComplianceControlFilter("");
  };

  const handleComplianceDialogControlChange = (e) => {
    const cid = e.target.value;
    setComplianceControlFilter(cid);
    if (!complianceDialogRow || !cid) return;
    const curFw = (complianceFrameworkFilter || "").trim();
    const fws = dialogFrameworksForControlId(complianceDialogRow, cid);
    if (curFw && !fws.includes(curFw)) {
      setComplianceFrameworkFilter(fws.length === 1 ? fws[0] : "");
    }
  };

  const renderHeader = () => {
    if (headerOnly && typeof onAccountChange === "function") {
      return (
        <FormControl
          size="small"
          fullWidth
          sx={{
            minWidth: 220,
            "& .MuiOutlinedInput-root": {
              fontFamily: t.fontFamily,
              fontSize: "0.875rem",
              backgroundColor: t.colors.card,
              borderRadius: t.radius.md,
              "& fieldset": { borderColor: t.colors.border },
              "&:hover fieldset": { borderColor: t.colors.textMuted },
              "&.Mui-focused fieldset": { borderColor: t.colors.accent, borderWidth: "1px" },
            },
          }}
        >
          <InputLabel id="detail-account-label" sx={{ fontFamily: t.fontFamily, fontSize: "0.8125rem" }}>
            View account
          </InputLabel>
          <Select
            labelId="detail-account-label"
            id="detail-account-select"
            value={accountNumber || ""}
            label="View account"
            onChange={(e) => onAccountChange(e.target.value)}
            sx={{ fontFamily: t.fontFamily, fontWeight: 500, "& .MuiSelect-select": { py: 1.25 } }}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 1.5,
                  borderRadius: t.radius.md,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  maxHeight: 320,
                  "& .MuiMenuItem-root": { fontFamily: t.fontFamily, fontSize: "0.875rem" },
                },
              },
            }}
          >
            {accountsLoading ? (
              <MenuItem value="" disabled>Loading…</MenuItem>
            ) : accountsError ? (
              <MenuItem value="" disabled>{accountsError}</MenuItem>
            ) : (
              accounts.map((acc, i) => {
                const num = acc?.account_number ?? acc?.accountNumber ?? acc?.id ?? "";
                if (!num) return null;
                return (
                  <MenuItem key={num || i} value={num}>
                    {typeof getAccountLabel === "function" ? getAccountLabel(acc) : num}
                  </MenuItem>
                );
              })
            )}
            {!accountsLoading && !accountsError && accounts.length === 0 && (
              <MenuItem value="" disabled>No accounts</MenuItem>
            )}
          </Select>
        </FormControl>
      );
    }

    if (showBackLink) {
      return (
        <Box sx={{ mb: 3 }}>
          <Typography
            component="span"
            onClick={() => history.push("/summary")}
            sx={{
              color: t.colors.accent,
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": { color: t.colors.accentHover, textDecoration: "underline" },
            }}
          >
            ← Back to ARN Insight
          </Typography>
        </Box>
      );
    }

    return null;
  };

  const handleOpenComplianceDetails = (row, options = {}) => {
    setComplianceDialogRow(row);
    setComplianceFrameworkFilter("");
    setComplianceControlFilter("");
    setComplianceDialogLayout(options.openedFrom === "control_id" ? "control_id" : "compliance");
  };

  const handleCloseComplianceDetails = () => {
    setComplianceDialogRow(null);
    setComplianceDialogLayout("compliance");
  };

  const tableCellSx = {
    fontFamily: t.fontFamily,
    fontSize: "0.8125rem",
    borderBottom: `1px solid ${t.colors.border}`,
    py: 1,
    px: 1.75,
  };
  const headerSx = {
    ...tableCellSx,
    backgroundColor: t.colors.borderLight,
    fontWeight: 600,
    color: t.colors.textMuted,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };

  if (!accountNumber) {
    return (
      <Box sx={{ fontFamily: t.fontFamily }}>
        {renderHeader()}
        <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
          <Typography sx={{ color: t.colors.textMuted, fontSize: "0.875rem" }}>
            {headerOnly
              ? "Select an account from the dropdown above to view details."
              : "Select an account to view details."}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ fontFamily: t.fontFamily }}>
      {renderHeader()}

      {servicesList.length > 0 && (
        <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: t.colors.text,
                fontFamily: t.fontFamily,
                mb: 1,
              }}
            >
              Filter ARNs table by service
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip
                label="All"
                size="small"
                onClick={() => {
                  setSelectedService("");
                  setDetailsPage(1);
                }}
                sx={{
                  fontFamily: t.fontFamily,
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  borderRadius: t.radius.sm,
                  bgcolor: !selectedService
                    ? t.colors.accent
                    : t.colors.borderLight,
                  color: !selectedService ? "#fff" : t.colors.text,
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: !selectedService
                      ? t.colors.accentHover
                      : "#e4e4e7",
                  },
                }}
              />
              {servicesList.map((item, i) => {
                const name = item?.service ?? item?.name ?? String(i);
                const total = item?.arns_total ?? item?.total ?? 0;
                const isActive = selectedService === name;
                return (
                  <Chip
                    key={i}
                    label={`${name}: ${total}`}
                    size="small"
                    onClick={() => {
                      setSelectedService(isActive ? "" : name);
                      setDetailsPage(1);
                    }}
                    sx={{
                      fontFamily: t.fontFamily,
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      borderRadius: t.radius.sm,
                      bgcolor: isActive
                        ? t.colors.accent
                        : t.colors.borderLight,
                      color: isActive ? "#fff" : t.colors.text,
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: isActive
                          ? t.colors.accentHover
                          : "#e4e4e7",
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
      )}

      {detailsError && (
        <Box sx={{ p: 2, mb: 2, borderRadius: t.radius.md, bgcolor: t.colors.destructiveBg, border: `1px solid ${t.colors.destructiveBorder}` }}>
          <Typography sx={{ fontSize: "0.875rem", color: t.colors.destructive, fontFamily: t.fontFamily }}>{detailsError}</Typography>
        </Box>
      )}

      {detailsLoading && (
        <Box sx={{ display: "flex", alignItems: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ color: t.colors.accent }} />
          <Typography sx={{ ml: 2, color: t.colors.textMuted, fontSize: "0.875rem", fontFamily: t.fontFamily }}>Loading ARNs...</Typography>
        </Box>
      )}

      {!detailsLoading && !detailsError && (
        <Box sx={{ backgroundColor: t.colors.card, borderRadius: t.radius.lg, border: `1px solid ${t.colors.border}`, boxShadow: t.shadow, overflow: "hidden" }}>
          {/* Client-side search + severity filter for table only */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${t.colors.border}`,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
              <SearchIcon sx={{ color: t.colors.textMuted, fontSize: 18 }} />
              <InputBase
                placeholder="Search by ARN, service, region, modules..."
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                size="small"
                sx={{
                  flex: 1,
                  maxWidth: 360,
                  fontSize: "0.8125rem",
                  fontFamily: t.fontFamily,
                  "& input": { py: 0.5 },
                }}
              />
              {tableSearch && (
                <Typography
                  component="span"
                  sx={{ fontSize: "0.75rem", color: t.colors.textMuted, fontFamily: t.fontFamily }}
                >
                  {filteredAndSortedDetails.length} of {details.length} on this page
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexShrink: 0,
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  component="span"
                  sx={{ fontSize: "0.75rem", color: t.colors.textMuted, fontFamily: t.fontFamily }}
                >
                  Severity:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={severity || ""}
                    onChange={(e) => {
                      const val = e.target.value || "";
                      if (typeof onSeverityChange === "function") {
                        onSeverityChange(val);
                      }
                    }}
                    displayEmpty
                    sx={{
                      fontSize: "0.8125rem",
                      height: 32,
                      fontFamily: t.fontFamily,
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.border },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.borderLight },
                    }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="CRITICAL">Critical</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="LOW">Low</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {Array.isArray(frameworks) && frameworks.length > 0 && typeof onFrameworkChange === "function" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.75rem", color: t.colors.textMuted, fontFamily: t.fontFamily }}
                  >
                    Framework:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <Select
                      value={framework || ""}
                      onChange={(e) => {
                        const val = e.target.value || "";
                        onFrameworkChange(val);
                      }}
                      displayEmpty
                      sx={{
                        fontSize: "0.8125rem",
                        height: 32,
                        fontFamily: t.fontFamily,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.border },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.borderLight },
                      }}
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      {frameworks.map((fw, idx) => {
                        const name = fw?.framework || fw?.name || fw || "";
                        if (!name) return null;
                        const iconSrc = getComplianceIcon(name);
                        return (
                          <MenuItem key={`${name}-${idx}`} value={name}>
                            {iconSrc && (
                              <Box
                                component="img"
                                src={iconSrc}
                                alt={name}
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: 1,
                                  objectFit: "contain",
                                  mr: 1,
                                }}
                              />
                            )}
                            {name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
              )}
              {kpiTableFilterLabel && typeof onClearKpiTableFilter === "function" && (
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                    alignSelf: "center",
                    minHeight: 28,
                    maxWidth: 220,
                    px: 1,
                    pr: 1.25,
                    py: 0.25,
                    borderRadius: t.radius.sm,
                    border: `1px solid ${t.colors.border}`,
                    backgroundColor: t.colors.borderLight,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 500,
                      color: t.colors.textMuted,
                      fontFamily: t.fontFamily,
                      lineHeight: 1.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={kpiTableFilterLabel}
                  >
                    {kpiTableFilterLabel}
                  </Typography>
                  <IconButton
                    type="button"
                    aria-label="Clear KPI filter"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClearKpiTableFilter();
                    }}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      width: 18,
                      height: 18,
                      minHeight: 18,
                      p: 0,
                      backgroundColor: t.colors.card,
                      border: `1px solid ${t.colors.border}`,
                      boxShadow: t.shadow,
                      color: t.colors.textMuted,
                      "&:hover": {
                        backgroundColor: t.colors.background,
                        color: t.colors.text,
                      },
                    }}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 11 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
          <TableContainer sx={{ maxHeight: "50vh" }}>
            <Table
              stickyHeader
              size="medium"
              sx={{
                tableLayout: "fixed",
                width: "100%",
                minWidth: 1000,
                fontFamily: t.fontFamily,
              }}
            >
              <TableHead>
                <TableRow>
                  {DETAIL_TABLE_COLUMNS.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align || "left"}
                      sx={{
                        ...headerSx,
                        width: col.width,
                        minWidth: col.minWidth,
                        maxWidth: col.width,
                        boxSizing: "border-box",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        verticalAlign: "bottom",
                      }}
                      onClick={() => handleSort(col.id)}
                    >
                      <Tooltip title={col.desc || ""} placement="top" arrow>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            flexDirection: col.labelSubtitle ? "column" : "row",
                            alignItems: col.align === "center" ? "center" : col.align === "right" ? "flex-end" : "flex-start",
                            gap: col.labelSubtitle ? 0.15 : 0.25,
                            justifyContent: col.align === "center" ? "center" : col.align === "right" ? "flex-end" : "flex-start",
                            width: col.align === "center" || col.align === "right" ? "100%" : "auto",
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.25,
                            }}
                          >
                            {col.label}
                            <InfoOutlinedIcon sx={{ fontSize: 14, color: t.colors.textMuted, ml: 0.25 }} />
                            {sortBy === col.id &&
                              (sortOrder === "asc" ? (
                                <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                              ) : (
                                <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                              ))}
                          </Box>
                          {col.labelSubtitle ? (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: "0.65rem",
                                fontWeight: 500,
                                color: t.colors.textMuted,
                                lineHeight: 1.2,
                                textTransform: "none",
                                letterSpacing: "0.02em",
                              }}
                            >
                              {col.labelSubtitle}
                            </Typography>
                          ) : null}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedDetails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={DETAIL_TABLE_COLUMNS.length} sx={{ ...tableCellSx, py: 6, textAlign: "center", color: t.colors.textMuted }}>
                      {tableSearch ? "No rows match your search on this page." : "No ARNs"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedDetails.map((row, i) => (
                    <TableRow
                      key={row.arn ?? i}
                      selected={selectedRow?.arn === row.arn}
                      onClick={() => setSelectedRow(selectedRow?.arn === row.arn ? null : row)}
                      sx={{
                        "& td": tableCellSx,
                        "&:hover": { bgcolor: t.colors.background },
                        cursor: "pointer",
                      }}
                    >
                      {DETAIL_TABLE_COLUMNS.map((col) => {
                        const align = col.align || "left";
                        const cellBase = {
                          ...tableCellSx,
                          width: col.width,
                          maxWidth: col.width,
                          minWidth: col.minWidth,
                          boxSizing: "border-box",
                          verticalAlign: "middle",
                          overflow: "hidden",
                        };
                        if (col.id === "arn") {
                          return (
                            <TableCell key={col.id} align={align} sx={{ ...cellBase, verticalAlign: "top" }}>
                              <Tooltip title={row.arn || ""} placement="top-start">
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: "0.8125rem",
                                    fontFamily: "monospace",
                                    color: t.colors.text,
                                    display: "block",
                                    whiteSpace: "normal",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {row.arn || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          );
                        }
                        if (col.id === "service") {
                          return (
                            <TableCell key={col.id} align={align} sx={{ ...cellBase, color: t.colors.text, fontWeight: 500 }}>
                              {row.service ?? "—"}
                            </TableCell>
                          );
                        }
                        if (col.id === "region") {
                          return (
                            <TableCell key={col.id} align={align} sx={{ ...cellBase, color: t.colors.textMuted }}>
                              {row.region ?? "—"}
                            </TableCell>
                          );
                        }
                        if (col.id === "security_failed_checks") {
                          return (
                            <TableCell key={col.id} align={align} sx={cellBase}>
                              <SecurityCell row={row} />
                            </TableCell>
                          );
                        }
                        if (col.id === "compliance_frameworks") {
                          return (
                            <TableCell key={col.id} align={align} sx={{ ...cellBase, fontFamily: t.fontFamily }}>
                              <ComplianceCell row={row} onOpenDetails={handleOpenComplianceDetails} />
                            </TableCell>
                          );
                        }
                        if (col.id === "compliance_control_id_count") {
                          return (
                            <TableCell key={col.id} align={align} sx={{ ...cellBase, fontFamily: t.fontFamily, fontVariantNumeric: "tabular-nums" }}>
                              <ControlIdCell row={row} onOpenDetails={handleOpenComplianceDetails} />
                            </TableCell>
                          );
                        }
                        if (col.id === "cloud_waste") {
                          return (
                            <TableCell key={col.id} align={align} sx={{ ...cellBase, fontFamily: t.fontFamily }}>
                              <CloudWasteCell row={row} />
                            </TableCell>
                          );
                        }
                        if (col.id === "cost_unblended_mtd") {
                          return (
                            <TableCell key={col.id} align={align} sx={{ ...cellBase, fontFamily: t.fontFamily, fontVariantNumeric: "tabular-nums" }}>
                              {formatCost(row.cost_unblended_mtd)}
                            </TableCell>
                          );
                        }
                        return null;
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Summary-style footer: Showing X–Y of Z, rows per page, Previous/Next */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `1px solid ${t.colors.border}`,
              backgroundColor: t.colors.card,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              borderRadius: "0 0 12px 12px",
            }}
          >
            <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.8125rem", fontWeight: 400, fontFamily: t.fontFamily }}>
              Showing {detailsTotal === 0 ? 0 : (detailsPage - 1) * detailsPageSize + 1}–
              {Math.min(detailsPage * detailsPageSize, detailsTotal)} of {detailsTotal}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", fontFamily: t.fontFamily }}>
                Rows per page
              </Typography>
              <FormControl size="small" sx={{ minWidth: 56 }}>
                <Select
                  value={detailsPageSize}
                  onChange={(e) => {
                    setDetailsPageSize(Number(e.target.value));
                    setDetailsPage(1);
                  }}
                  sx={{
                    fontSize: "0.8125rem",
                    height: 32,
                    fontFamily: t.fontFamily,
                    color: t.colors.text,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.border },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.borderLight },
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n} sx={{ fontFamily: t.fontFamily, fontSize: "0.8125rem" }}>{n}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                disabled={detailsPage <= 1}
                onClick={() => setDetailsPage((p) => p - 1)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  borderColor: t.colors.border,
                  color: t.colors.textMuted,
                  minWidth: "auto",
                  px: 1.5,
                  fontFamily: t.fontFamily,
                  "&:hover": { borderColor: t.colors.borderLight, backgroundColor: t.colors.background },
                  "&.Mui-disabled": { borderColor: t.colors.borderLight, color: t.colors.textMutedLight },
                }}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={detailsPage * detailsPageSize >= detailsTotal}
                onClick={() => setDetailsPage((p) => p + 1)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  borderColor: t.colors.border,
                  color: t.colors.textMuted,
                  minWidth: "auto",
                  px: 1.5,
                  fontFamily: t.fontFamily,
                  "&:hover": { borderColor: t.colors.borderLight, backgroundColor: t.colors.background },
                  "&.Mui-disabled": { borderColor: t.colors.borderLight, color: t.colors.textMutedLight },
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Dialog
        open={!!complianceDialogRow}
        onClose={handleCloseComplianceDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: t.radius.lg,
            boxShadow:
              "0 24px 48px rgba(15,23,42,0.25), 0 0 0 1px rgba(15,23,42,0.04)",
            border: `1px solid ${t.colors.border}`,
            backgroundColor: t.colors.card,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: t.fontFamily,
            fontSize: "0.9375rem",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${t.colors.border}`,
            backgroundColor: t.colors.card,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
            <Typography
              component="span"
              sx={{
                fontFamily: t.fontFamily,
                fontSize: "0.9375rem",
                fontWeight: 600,
              }}
            >
              Compliance details
            </Typography>
            {complianceDialogRow?.arn && (
              <Typography
                component="span"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  color: t.colors.textMuted,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={complianceDialogRow.arn}
              >
                {complianceDialogRow.arn}
              </Typography>
            )}
          </Box>
          <IconButton
            edge="end"
            aria-label="Close"
            onClick={handleCloseComplianceDetails}
            sx={{
              ml: 1,
              color: t.colors.textMuted,
              "&:hover": { color: t.colors.text },
            }}
          >
            {/* Close icon kept simple to match shadcn-style dialogs */}
            <span style={{ fontSize: 18 }}>×</span>
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 2,
            pt: 2.25,
            pb: 2.5,
            backgroundColor: t.colors.background,
          }}
        >
          {complianceDialogRow && (() => {
            const dialogFocusControlFirst = complianceDialogLayout === "control_id";
            const dialogColumnOrder = dialogFocusControlFirst
              ? ["control_ids", "framework", "remediation"]
              : ["framework", "control_ids", "remediation"];
            const dialogColumnWidths = dialogFocusControlFirst
              ? { framework: "40%", control_ids: "28%", remediation: "32%" }
              : { framework: "45%", control_ids: "25%", remediation: "30%" };
            const dialogColumnLabels = {
              framework: "Framework",
              control_ids: "Control IDs",
              remediation: "Remediation",
            };

            const frameworkFilterControl = (
              <FormControl key="fw" size="small" sx={{ minWidth: 200 }}>
                <InputLabel sx={{ fontFamily: t.fontFamily, fontSize: "0.78rem" }}>Framework</InputLabel>
                <Select
                  label="Framework"
                  value={complianceFrameworkFilter}
                  onChange={handleComplianceDialogFrameworkChange}
                  sx={{ fontFamily: t.fontFamily, fontSize: "0.8rem" }}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {complianceDialogFrameworkOptions.map((fw) => {
                    const iconSrc = getComplianceIcon(fw) || fallbackComplianceIcon;
                    return (
                      <MenuItem key={fw} value={fw}>
                        <Box
                          component="img"
                          src={iconSrc}
                          alt={fw || "aws"}
                          sx={{ width: 18, height: 18, borderRadius: 1, objectFit: "contain", mr: 1 }}
                        />
                        {fw}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            );

            const controlFilterControl = (
              <FormControl key="cid" size="small" sx={{ minWidth: 200 }}>
                <InputLabel sx={{ fontFamily: t.fontFamily, fontSize: "0.78rem" }}>Control ID</InputLabel>
                <Select
                  label="Control ID"
                  value={complianceControlFilter}
                  onChange={handleComplianceDialogControlChange}
                  sx={{ fontFamily: t.fontFamily, fontSize: "0.8rem" }}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {complianceDialogControlOptions.map((cid) => (
                    <MenuItem key={cid} value={cid}>
                      {cid}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );

            const renderDialogDataCell = (colKey, item) => {
              const cellSx = {
                fontFamily: t.fontFamily,
                fontSize: "0.78rem",
                py: 1,
                px: 1.5,
              };
              if (colKey === "framework") {
                return (
                  <TableCell key={colKey} sx={cellSx}>
                    {item.framework || "—"}
                  </TableCell>
                );
              }
              if (colKey === "control_ids") {
                return (
                  <TableCell key={colKey} sx={cellSx}>
                    {Array.isArray(item.control_ids) && item.control_ids.length > 0
                      ? item.control_ids.join(", ")
                      : "—"}
                  </TableCell>
                );
              }
              return (
                <TableCell key={colKey} sx={cellSx}>
                  {item.url ? (
                    <Typography
                      component="a"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontFamily: t.fontFamily,
                        fontSize: "0.78rem",
                        color: t.colors.accent,
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      View remediation
                    </Typography>
                  ) : (
                    item.title || "—"
                  )}
                </TableCell>
              );
            };

            return (
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    mt: 0.75,
                    mb: 1.75,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {dialogFocusControlFirst
                    ? [controlFilterControl, frameworkFilterControl]
                    : [frameworkFilterControl, controlFilterControl]}
                </Box>
                <TableContainer
                  sx={{
                    maxHeight: 320,
                    borderRadius: t.radius.md,
                    border: `1px solid ${t.colors.border}`,
                    backgroundColor: t.colors.card,
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {dialogColumnOrder.map((colKey) => (
                          <TableCell
                            key={colKey}
                            sx={{
                              fontFamily: t.fontFamily,
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              backgroundColor: t.colors.borderLight,
                              py: 1,
                              px: 1.5,
                              borderBottom: `1px solid ${t.colors.border}`,
                              width: dialogColumnWidths[colKey],
                            }}
                          >
                            {dialogColumnLabels[colKey]}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {complianceDialogRemediations.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            sx={{
                              fontFamily: t.fontFamily,
                              fontSize: "0.8rem",
                              color: t.colors.textMuted,
                              textAlign: "center",
                              py: 3,
                            }}
                          >
                            No remediations match the current filters.
                          </TableCell>
                        </TableRow>
                      ) : (
                        complianceDialogRemediations.map((item, idx) => (
                          <TableRow
                            key={idx}
                            sx={{
                              "&:last-of-type td": { borderBottom: "none" },
                              "& td": { borderBottom: `1px solid ${t.colors.borderLight}` },
                            }}
                          >
                            {dialogColumnOrder.map((colKey) => renderDialogDataCell(colKey, item))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseComplianceDetails}
            sx={{
              textTransform: "none",
              fontFamily: t.fontFamily,
              fontSize: "0.8rem",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
